// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/badge.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import { Badge as BadgeV1 } from "@repo/design-system/components/ui/badge";
import { cn } from "@repo/design-system/lib/utils";
import type { ComponentProps } from "react";

/**
 * v2 Badge — restyles v1's `Badge` (`components/ui/badge.tsx`) to match
 * the Figma "Remote Leverage — Design System" file, Badge component node
 * `44:1521`. Rendering (asChild/Slot, focus-visible, aria-invalid) stays
 * v1's own — this file only overrides the visual layer via a new `color`
 * prop (Figma's 7 semantic status colors), replacing v1's `variant` axis
 * rather than mapping onto it, since the two enumerations don't
 * correspond 1:1 (v1: default/secondary/destructive/outline vs. Figma's
 * status colors). Whichever v1 `variant` ends up applied underneath,
 * `color`'s bg/text classes are in the same Tailwind class groups so
 * they cleanly replace it — `variant` is omitted from this file's prop
 * type since `color` is the axis this design maps to.
 *
 * Figma's pill radius (20px) is numerically larger than half of either
 * size's height (26px/20px), which renders identically to v1's existing
 * `rounded-full` — no radius override needed.
 *
 * Deliberately out of scope:
 * - Figma's asymmetric `sm` vertical padding (4px top / 5px bottom) is
 *   dropped in favor of v1's existing symmetric padding — with an
 *   explicit fixed height and v1's own `items-center`, content still
 *   renders centered either way, and it avoids depending on Tailwind's
 *   un-merged `py-*` vs `pt-*`/`pb-*` cascade order for a 1px difference
 *   (confirmed via a direct tailwind-merge check that `py-0.5` and
 *   `pt-[4px]`/`pb-[5px]` are NOT deduplicated the way border/radius
 *   utilities are).
 * - `leftIcon`/`rightIcon` slots only exist on Figma's `lg` size (no
 *   `sm` instance shows one) — `sm` badges can still take an `<svg>`
 *   child like v1 always could, it just isn't a named prop here.
 */

const COLOR_CLASSES = {
  green: "bg-(--v2-badge-green-bg) text-(--v2-badge-green-text)",
  purple: "bg-(--v2-badge-purple-bg) text-(--v2-badge-purple-text)",
  gray: "bg-(--v2-badge-gray-bg) text-(--v2-badge-gray-text)",
  darkGray: "bg-(--v2-badge-dark-gray-bg) text-(--v2-badge-dark-gray-text)",
  blue: "bg-(--v2-badge-blue-bg) text-(--v2-badge-blue-text)",
  orange: "bg-(--v2-badge-orange-bg) text-(--v2-badge-orange-text)",
  red: "bg-(--v2-badge-red-bg) text-(--v2-badge-red-text)",
} as const;

const SIZE_CLASSES = {
  lg: "h-[26px] text-xs [&>svg]:size-3.5",
  sm: "h-[20px] text-[11px]/[1.56]",
} as const;

type BadgeV2Color = keyof typeof COLOR_CLASSES;
type BadgeV2Size = keyof typeof SIZE_CLASSES;

type BadgeProps = Omit<ComponentProps<typeof BadgeV1>, "variant"> & {
  color?: BadgeV2Color;
  size?: BadgeV2Size;
};

function Badge({
  className,
  color = "green",
  size = "lg",
  ...props
}: BadgeProps) {
  return (
    <BadgeV1
      className={cn(
        "ds-v2 border-transparent",
        COLOR_CLASSES[color],
        SIZE_CLASSES[size],
        className
      )}
      {...props}
    />
  );
}

export { Badge };
export type { BadgeProps };
